import { Component, Inject, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ENV_CONFIG } from 'src/app/core/injection.token';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ImageCaptureModalComponent } from 'src/app/extensions/components/image-capture-modal/image-capture-modal.component';
import {
  FILE_UPLOAD_ERROR_CODE,
  REQUEST_DETAIL_CATEGORY,
  UPLOAD_MODE,
} from 'src/app/extensions/constants/commonConstants';
import { DictionaryCommentType } from 'src/app/extensions/constants/dictionaryConstants/dictionaryConstants';
import { FileEvents, RequestCommentEvents } from 'src/app/extensions/services/api/events';
import { FileStoreAPIService } from 'src/app/extensions/services/api/file-store-api.service';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { isNotEmpty } from 'src/app/extensions/utilities';

@Component({
  selector: 'request-comment',
  templateUrl: './request-comment.component.html',
  styleUrl: './request-comment.component.scss',
  standalone: false,
})
export class RequestCommentComponent implements OnInit {
  @Input() requestId: string;
  @Input() requestTypeCode: string;
  @Input() selectedCategory: string;

  REQUEST_DETAIL_CATEGORY = REQUEST_DETAIL_CATEGORY;

  requestCommentsDataLoaded: boolean = false;
  requestComments: app.models.RequestComment[] = [];
  newComment: string;
  messageToSendCommentTypeCode: string = DictionaryCommentType.TEXT;
  toUploadDocuments: app.models.RequestXDocument[] = [];

  UPLOAD_ALLOWED_EXTENSIONS = [
    ...this._envConfig.ALLOWED_FILE_EXTENSIONS.DOCUMENTS,
    ...this._envConfig.ALLOWED_FILE_EXTENSIONS.IMAGES,
  ].map(extension => extension.toLowerCase());

  acceptExtensions = this.UPLOAD_ALLOWED_EXTENSIONS.map(extension => `.${extension}`).join(',');

  constructor(
    private _requestAPIService: RequestAPIService,
    private _notificationService: NotificationService,
    private _translateService: TranslateService,
    private _fileStoreAPIService: FileStoreAPIService,
    private _ngbModal: NgbModal,
    @Inject(ENV_CONFIG) private _envConfig: env.EnvConfigurationData
  ) {}

  ngOnInit(): void {
    this._requestAPIService.getRequestComments(this.requestId, this.requestTypeCode).subscribe(requestComments => {
      this.requestComments = requestComments.data;
      this.requestCommentsDataLoaded = true;
    });
  }

  saveCommentHandler() {
    this._requestAPIService
      .saveRequestComment(
        this.requestId,
        this.newComment,
        this.requestTypeCode,
        this.messageToSendCommentTypeCode,
        this.toUploadDocuments
      )
      .subscribe(x => {
        if (x.isSuccess) {
          this._notificationService.showSuccess(
            this._translateService.instant('requests.clientData.notification.commentPosted'),
            this._translateService.instant('common.success')
          );

          const saveRequestCommentEventObject = x.response.events.find(
            x => x.eventType == RequestCommentEvents.RequestCommentSaved
          );
          this.requestComments.push(saveRequestCommentEventObject.eventBody);

          this.newComment = undefined;
          this.toUploadDocuments = [];
          this.messageToSendCommentTypeCode = DictionaryCommentType.TEXT;

          setTimeout(() => {
            let commentHistoryContainer = document.getElementById('comment-history-container');
            commentHistoryContainer.scrollTop = commentHistoryContainer.scrollHeight;
          }, 0);
        } else {
          this._notificationService.showWarning(
            this._translateService.instant('common.warning'),
            this._translateService.instant('common.warning')
          );
        }
      });
  }

  UPLOAD_MODE = UPLOAD_MODE;
  uploadFileInStoreHandler(event: any, mode: string) {
    //prevent file open in browser
    if (mode == UPLOAD_MODE.DRAG_AND_DROP) {
      event.preventDefault();
      event.stopPropagation();
    }

    const file = (mode == UPLOAD_MODE.DRAG_AND_DROP ? event.dataTransfer.files[0] : event.target.files[0]) as File;
    let fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (!fileExtension || !this.UPLOAD_ALLOWED_EXTENSIONS.includes(fileExtension)) {
      this._notificationService.showWarning(
        this._translateService.instant('common.uploadedFileWrongExtension'),
        this._translateService.instant('common.warning')
      );
      return;
    }

    const allowedFileSizeinBytes = this._envConfig.ALLOWED_FILE_SIZE * 1000000;
    if (file.size > allowedFileSizeinBytes) {
      this._notificationService.showWarning(
        this._translateService.instant('common.fileSizeExceeded', { maxSize: this._envConfig.ALLOWED_FILE_SIZE }),
        this._translateService.instant('common.warning')
      );
      return;
    }

    this.uploadFile(file);
  }

  private uploadFile(file: File) {
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);

    this._fileStoreAPIService.postFilesWithEvents(formData).subscribe(response => {
      if (isNotEmpty(response)) {
        const failEvents = response.events.filter(x => x.eventType == FileEvents.FilePostFailed);

        if (failEvents.length > 0) {
          const eventCodes = failEvents.map(e => e.eventBody.eventCode);

          let notification = '';

          if (eventCodes.includes(FILE_UPLOAD_ERROR_CODE.VIRUS)) {
            notification =
              failEvents.length > 1 ? 'common.uploadedFilesContainsVirus' : 'common.uploadedFileContainsVirus';
          } else if (eventCodes.includes(FILE_UPLOAD_ERROR_CODE.INVALID_FILE_TYPE)) {
            notification = failEvents.length > 1 ? 'common.uploadedFilesInvalidType' : 'common.uploadedFileInvalidType';
          }

          if (notification) {
            this._notificationService.showWarning(
              this._translateService.instant(notification),
              this._translateService.instant('common.warning')
            );
          }
          return;
        }
        response.events.forEach(event => {
          const uploadedFiles = event.eventBody.uploadedFiles;
          const [key, value] = Object.entries(uploadedFiles)[0];
          const reader = new FileReader();
          reader.onload = (readerEvent: any) => {
            this.toUploadDocuments.push({
              documentName: value,
              documentSize: readerEvent.total,
              documentStoragePath: key,
            } as app.models.RequestXDocument);
          };
          reader.readAsDataURL(file);
          this.messageToSendCommentTypeCode = DictionaryCommentType.DOCUMENT;
        });
      }
    });
  }

  downloadDocument(file: app.models.RequestXDocument) {
    if (file.documentStoragePath) {
      this._fileStoreAPIService.getFile(file.documentStoragePath).subscribe(response => {
        if (response) {
          const documentRef = `data:application/pdf;base64,${response}`;
          const downloadLink = document.createElement('a');

          downloadLink.href = documentRef;
          downloadLink.download = file.documentName;
          downloadLink.click();
        }
      });
    }
  }

  removeDocumentHandler(document: app.models.RequestXDocument) {
    this.toUploadDocuments = this.toUploadDocuments.filter(x => x.documentStoragePath != document.documentStoragePath);
  }

  openImageCaptureModal() {
    let modal = this._ngbModal.open(ImageCaptureModalComponent, { centered: true, backdrop: 'static' });

    modal.result.then(result => {
      if (result) {
        this.uploadFile(result);
      }
    });
  }
}
